import {t} from "@lingui/core/macro";
import {useCallback} from "react";
import {useLocation, useNavigate} from "react-router-dom-v5-compat";

import {publishAnalyticsEventForMinorConversion} from "@repo/analytics-learning-time";
import {setPromptVariables} from "@repo/guide-core-helpers";
import {GuideStateApi} from "@repo/guide-state";
import {ActionType} from "@repo/guide-types";
import Log, {Errors} from "@repo/logging";

import {appendConversationWithErrorMessage} from "../internal/append-conversation-with-error-message.ts";
import {ResumeUnownedThreadError} from "../internal/errors.ts";
import {useCloseThreadMutation} from "../queries/use-close-thread-mutation.ts";

import {useInitializeGuide} from "./use-initialize-guide.ts";
import {useSendGuideEvent} from "./use-send-guide-event.ts";

interface ResumeConversationFn {
    /**
     * Resumes a conversation with the AI guide for the given threadId.
     *
     * @returns A promise that resolves when the conversation has been resumed.
     */
    (threadId: string): Promise<void>;
}

/**
 * Obtain a function to resume a conversation for a given threadId.
 *
 * @returns A function to resume a conversation for a given threadId.
 */
export const useResumeConversation = (): ResumeConversationFn => {
    const sendGuideEvent = useSendGuideEvent();
    const closeThread = useCloseThreadMutation();
    const initializeGuide = useInitializeGuide();

    const {search} = useLocation();
    const navigate = useNavigate();

    const resumeConversationFn = useCallback(
        async (threadId: string) => {
            // Close current conversation if one exists
            const currentThreadId = GuideStateApi.latest().threadId;
            if (currentThreadId) {
                closeThread(currentThreadId);
            }

            // Set threadId so that it is passed with the event,
            // clear conversation, and set loading state
            GuideStateApi.set("threadId", threadId);
            GuideStateApi.set("conversation", []);
            GuideStateApi.set("isLoading", true);

            try {
                await sendGuideEvent("resume_conversation", {
                    onResponseComplete: ({
                        conversation,
                        feedbackMap,
                        restoredPromptVariables,
                    }) => {
                        if (restoredPromptVariables) {
                            setPromptVariables(restoredPromptVariables);
                        }

                        GuideStateApi.set("conversation", conversation);
                        GuideStateApi.set(
                            "ratedInteractions",
                            feedbackMap ?? new Map(),
                        );
                    },
                });
            } catch (error: any) {
                // If the user attempts to resume a thread they do not own,
                // clear the query param and initialize a new conversation
                if (error === ResumeUnownedThreadError) {
                    const queryParams = new URLSearchParams(search);
                    queryParams.delete("resumeThreadId");
                    navigate({search: queryParams.toString()}, {replace: true});

                    initializeGuide();

                    return;
                }

                // For any other errors, add an error message in Khanmigo
                // with an action bubble for starting a new chat
                GuideStateApi.set(
                    "conversation",
                    appendConversationWithErrorMessage(
                        [],
                        t`An error occurred while resuming this conversation. Refresh the page to try again, or start a new chat.`,
                        [
                            {
                                label: t`Start a new chat`,
                                command: "newChat",
                                actions: [
                                    {
                                        actionType: ActionType.newChat,
                                        parameters: {},
                                    },
                                ],
                            },
                        ],
                    ),
                );

                Log.error(
                    "Failed to send resume_conversation event to ai-guide.",
                    Errors.TransientKhanService,
                    {cause: error},
                );
            } finally {
                GuideStateApi.set("isLoading", false);
            }

            // Once response has been received, conversation
            // is initialized
            GuideStateApi.set("initialized", true);

            publishAnalyticsEventForMinorConversion({
                eventType: "AiGuideChatHistoryConversationResumed",
                eventSchemaVersion: 1,
                aiGuideThreadId: threadId,
                aiGuideNavigationId: GuideStateApi.latest().navigation.id,
            });
        },
        [sendGuideEvent, closeThread, initializeGuide, navigate, search],
    );

    return resumeConversationFn;
};
